import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  buildSnapshotGroups,
  snapshotHasValues,
  type VysorSessionSnapshot,
} from '../../utils/vysorSessionSnapshot';
import {
  LAB,
  labHairline,
  labInk,
  labMuted,
  labSurface,
} from './vysorLabTheme';

type Props = {
  snapshot: VysorSessionSnapshot | null;
  mode: 'light' | 'dark';
  dense?: boolean;
};

const VysorContextPanel: React.FC<Props> = ({ snapshot, mode, dense }) => {
  const groups = buildSnapshotGroups(snapshot);
  const hasValues = snapshotHasValues(snapshot);
  const ink = labInk(mode);
  const muted = labMuted(mode);
  const hairline = labHairline(mode);
  const surface = labSurface(mode);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        p: dense ? 1.25 : 1.75,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: `${LAB.radius.panel}px`,
          border: `1px solid ${hairline}`,
          bgcolor: surface,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2.1, pt: 2, pb: 1.35 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              sx={{
                fontFamily: LAB.fontBody,
                fontSize: '0.95rem',
                fontWeight: 650,
                letterSpacing: '-0.02em',
                color: ink,
              }}
            >
              Context
            </Typography>
            {hasValues && (
              <Box
                sx={{
                  px: 0.85,
                  py: 0.2,
                  borderRadius: '8px',
                  bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                  fontFamily: LAB.fontBody,
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  color: muted,
                }}
              >
                {groups.reduce((n, g) => n + g.rows.length, 0)} fields
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              mt: 0.35,
              fontFamily: LAB.fontBody,
              fontSize: '0.78rem',
              color: muted,
              lineHeight: 1.4,
            }}
          >
            Locked from this conversation
          </Typography>
        </Box>

        <Box sx={{ mx: 2.1, height: 1, bgcolor: hairline }} />

        <Box sx={{ flex: 1, overflow: 'auto', px: 2.1, py: 1.85 }}>
          {!hasValues ? (
            <Box
              sx={{
                py: 4.5,
                px: 1,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '12px',
                  border: `1px dashed ${hairline}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: muted,
                  fontFamily: LAB.fontBody,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                ···
              </Box>
              <Typography
                sx={{
                  fontFamily: LAB.fontBody,
                  fontSize: '0.88rem',
                  color: muted,
                  lineHeight: 1.5,
                }}
              >
                Values appear as you lock
                <br />
                app, environment, and protocol.
              </Typography>
            </Box>
          ) : (
            groups.map((group, gi) => (
              <Box key={group.id} sx={{ mb: gi < groups.length - 1 ? 2.5 : 0 }}>
                <Typography
                  sx={{
                    fontFamily: LAB.fontBody,
                    fontSize: '0.66rem',
                    fontWeight: 650,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: muted,
                    mb: 1,
                  }}
                >
                  {group.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {group.rows.map((row) => (
                    <Box key={row.key}>
                      <Typography
                        sx={{
                          fontFamily: LAB.fontBody,
                          fontSize: '0.7rem',
                          color: muted,
                          mb: 0.15,
                        }}
                      >
                        {row.label}
                      </Typography>
                      {row.key === 'missing' ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55 }}>
                          {row.value.split(',').map((m) => (
                            <Box
                              key={m.trim()}
                              sx={{
                                px: 0.9,
                                py: 0.3,
                                borderRadius: '8px',
                                border: `1px solid ${hairline}`,
                                fontFamily: LAB.fontBody,
                                fontSize: '0.74rem',
                                color: ink,
                              }}
                            >
                              {m.trim()}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontFamily:
                              row.key === 'clientId' ||
                              row.key === 'entityId' ||
                              row.key === 'acs' ||
                              row.key === 'discovery' ||
                              row.key === 'redirects'
                                ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
                                : LAB.fontBody,
                            fontSize: '0.88rem',
                            fontWeight: 520,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.45,
                            color: ink,
                          }}
                        >
                          {row.value}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VysorContextPanel;
